import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronRight } from "lucide-react";
import RoleHomeShell from "./RoleHomeShell";
import { Badge } from "../lib/ui";
import { g1Readiness, isPreG1 } from "../lib/g1Journey";
import { withApp } from "../lib/story";
import G1FlowDiagram from "./G1FlowDiagram";
import PersonaAppPanel from "./PersonaAppPanel";
import { resolvePersona } from "../lib/personas";

function barTone(pct) {
  if (pct >= 80) return "green";
  if (pct >= 50) return "cyan";
  if (pct >= 25) return "amber";
  return "slate";
}

/** Application landing before G1 PASS — readiness, domain bars, next action. */
export default function ApplicationOverview({ app, portfolio, user }) {
  if (!app) return null;
  const guide = user?.guide || {};
  const g1 = g1Readiness(app) || {};
  const pre = isPreG1(app);
  const persona = resolvePersona(guide);
  const showPersonaApp = !pre || ["enterprise_architect", "solution_architect", "security_architect", "developer", "tester", "business_owner", "application_owner"].includes(persona.id);
  const next = g1.next_best_action || {};
  const steps = g1.pre_g1_steps || [];
  const eq = g1.evidence_quality || {};

  return (
    <div className="dashboard inner-page subflow-page" data-testid="application-overview">
      <RoleHomeShell guide={guide} portfolio={portfolio} app={app} appId={app.id} count={portfolio?.applications?.length} />

      {showPersonaApp && <PersonaAppPanel app={app} guide={guide} portfolio={portfolio} />}

      <div className="intro-row">
        <div>
          <p className="kicker">APPLICATION · {app.id} · {pre ? "PRE-G1" : "POST-G1"}</p>
          <h1>{app.name}</h1>
          <p className="intro-copy muted small-note">{g1.definition || "Modernization Intent & WHY Confirmation"}</p>
        </div>
        <Badge tone={g1.status === "PASS" ? "green" : g1.status === "BLOCKED" ? "rose" : "amber"} data-testid="g1-status-badge">
          G1 · {(g1.status || "NOT_READY").replaceAll("_", " ")}
        </Badge>
      </div>

      <section className="feature-panel analytics-panel" data-testid="g1-readiness-panel">
        <div className="panel-header compact">
          <div>
            <p className="kicker">G1 DECISION READINESS · MINIMUM SUFFICIENT EVIDENCE</p>
            <h2>{eq.decision_readiness?.pct ?? g1.readiness_pct ?? 0}% ready for G1 review</h2>
          </div>
          <strong className="cyan-strong">{g1.g1_state || "NOT_STARTED"}</strong>
        </div>
        <div className="readiness-bar-track" data-testid="g1-readiness-bar">
          <span
            className="readiness-bar-fill"
            style={{ width: `${eq.decision_readiness?.pct ?? g1.readiness_pct ?? 0}%` }}
          />
        </div>
        <div className="metric-grid">
          <div className="metric" data-testid="metric-completeness">
            <div><small>Completeness</small><strong>{eq.completeness?.pct ?? g1.coverage ?? 0}%</strong></div>
            <p className="muted small-note">{eq.completeness?.note || "Domain coverage — not confidence."}</p>
          </div>
          <div className="metric" data-testid="metric-confidence">
            <div><small>Confidence</small><strong>{eq.confidence?.pct ?? 0}%</strong></div>
            <p className="muted small-note">{eq.confidence?.note || "Verified facts minus conflicts."}</p>
          </div>
          <div className="metric" data-testid="metric-decision-readiness">
            <div><small>G1 readiness</small><strong>{eq.decision_readiness?.pct ?? g1.readiness_pct ?? 0}%</strong></div>
            <p className="muted small-note">{eq.decision_readiness?.note || "Not a full modernization assessment."}</p>
          </div>
        </div>
        <div className="dist-list">
          {(g1.domain_bars || []).map((row) => (
            <div key={row.id} data-testid={`g1-domain-${row.id}`}>
              <span className="dist-label">{row.label}</span>
              <span className="dist-bar-wrap">
                <span className={`dist-bar ${barTone(row.pct)}`} style={{ width: `${row.pct}%` }} />
              </span>
              <b>{row.pct}%</b>
            </div>
          ))}
        </div>
      </section>

      {pre && <G1FlowDiagram g1={g1} appId={app.id} />}

      <section className="feature-panel analytics-panel" data-testid="g1-next-action">
        <p className="kicker">NEXT BEST ACTION</p>
        <strong>{next.label || "Start discovery"}</strong>
        <p className="muted small-note">{next.detail}</p>
        {next.route && (
          <Link className="primary-button" to={withApp(next.route, app.id)} data-testid="g1-next-action-link">
            {next.label} <ArrowUpRight size={15} />
          </Link>
        )}
      </section>

      {(g1.missing || []).length > 0 && (
        <section className="admin-section" data-testid="g1-missing">
          <p className="kicker">MISSING FOR G1</p>
          <ul className="admin-list">
            {g1.missing.map((item) => <li key={item}>{item}</li>)}
          </ul>
        </section>
      )}

      <section className="feature-panel analytics-panel" data-testid="g1-open-items">
        <p className="kicker">OPEN ITEMS</p>
        <div className="metric-grid">
          <div className="metric"><div><small>Evidence gaps</small><strong>{g1.open_items?.gaps ?? 0}</strong></div></div>
          <div className="metric"><div><small>Conflicts</small><strong>{g1.open_items?.conflicts ?? 0}</strong></div></div>
          <div className="metric"><div><small>Confirmations</small><strong>{g1.open_items?.confirmations ?? 0}</strong></div></div>
          <div className="metric"><div><small>WHY</small><strong>{g1.why_state || "—"}</strong></div></div>
        </div>
      </section>

      {pre && steps.length > 0 && (
        <section className="admin-section" data-testid="g1-journey-steps">
          <p className="kicker">CURRENT STEP · {g1.current_step || "—"}</p>
          <p className="muted small-note">
            Discovery runs in parallel: evidence, pains and drivers converge into WHY, then outcomes and G1 readiness.
          </p>
        </section>
      )}

      {pre && (
        <p className="admin-note" data-testid="g1-freeze-note">
          Workflow: {g1.sequence}. Post-G1 (7R, MDR, G2–G8) stays hidden until G1 PASS.
        </p>
      )}
    </div>
  );
}
